import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchTrip, useUpdateTrip } from 'hooks/api/trips';
import { notify } from 'services/notifications';
import { Scaffold } from 'layouts';
import { Button } from 'components/ui/button';
import {
  ActionsMenu,
  type MenuAction,
} from 'pages/tracking-v2/shared-components';

import { useTripStore } from './hooks';
import {
  PageHeader,
  TripMap,
  TripInfoBar,
  InvoicesList,
  UpdateTripDriverNameDialog,
  type UpdateTripDriverNameDialogRef,
  UpdateTripVehiclePlateDialog,
  type UpdateTripVehiclePlateDialogRef,
  ActivitiesTimeline,
} from './components';
import { ConfirmationDialog, ConfirmationDialogRef } from 'components/Shared';
import { useDeleteTrip } from 'hooks/api/trips/use-delete-trip';
import { UpdateDeadlineDateDialog, UpdateDeadlineDateDialogRef } from './components/update-deadline-date-dialog';

export const TripDetailsPage = () => {
  const { id: tripId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setTrip, resetTripStoreState } = useTripStore();
  const { fetchTrip } = useFetchTrip();
  const { updateTrip } = useUpdateTrip();
  const { deleteTrip } = useDeleteTrip();

  const updateDriverNameDialogRef =
    React.useRef<UpdateTripDriverNameDialogRef>(null);
  const updateDeadlineDateDialogRef =
    React.useRef<UpdateDeadlineDateDialogRef>(null);
  const updateVehiclePlateDialogRef =
    React.useRef<UpdateTripVehiclePlateDialogRef>(null);
  const deleteTripDialogRef = React.useRef<ConfirmationDialogRef>(null);

  const tripQuery = useQuery({
    queryKey: ['fetch-trip', tripId],
    queryFn: () => fetchTrip(tripId!),
    onSuccess: setTrip,
    onError: (e: any) => {
      notify('error', e?.message);
    },
    refetchOnWindowFocus: false,
  });

  const updateDriverNameMutation = useMutation({
    mutationKey: ['update-trip-driver-name'],
    mutationFn: updateTrip,
    onSuccess: async (updatedTrip) => {
      setTrip(updatedTrip);
      updateDriverNameDialogRef.current?.close();
      notify('success', 'Nome do motorista alterado com sucesso!');
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const updateDeadlineDateMutation = useMutation({
    mutationKey: ['update-trip-deadline-date'],
    mutationFn: updateTrip,
    onSuccess: async (updatedTrip) => {
      setTrip(updatedTrip);
      updateDeadlineDateDialogRef.current?.close();
      notify('success', 'Prazo alterado com sucesso!');
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const updateVehiclePlateMutation = useMutation({
    mutationKey: ['update-trip-vehicle-plate'],
    mutationFn: updateTrip,
    onSuccess: async (updatedTrip) => {
      setTrip(updatedTrip);
      updateVehiclePlateDialogRef.current?.close();
      notify('success', 'Placa do veículo alterada com sucesso!');
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const deleteTripMutation = useMutation({
    mutationKey: ['delete-trip'],
    mutationFn: async (id: string) => {
      return deleteTrip(id);
    },
    onSuccess: async () => {
      notify('success', 'Viagem removida com sucesso!');
      navigate(-1);
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const handleDelete = React.useCallback(async (): Promise<void> => {
    if (!tripId) return;

    const confirmed = await deleteTripDialogRef.current?.openDialog({
      title: 'Deseja realmente apagar essa viagem?',
      message: 'Esta ação não poderá ser desfeita após confirmada.',
      confirmButtonMood: 'outlinedDanger',
    });
    
    if (confirmed) deleteTripMutation.mutate(tripId);
  }, [tripId]);

  const menuActions = React.useMemo((): MenuAction[] => {
    const trip = tripQuery?.data;

    return [
      {
        label: 'Alterar nome do motorista',
        onClick: updateDriverNameDialogRef.current?.open,
        disabled: !trip,
      },
      {
        label: 'Alterar placa do veículo',
        onClick: updateVehiclePlateDialogRef.current?.open,
        disabled: !trip,
      },
      {
        label: 'Alterar prazo da viagem',
        onClick: updateDeadlineDateDialogRef.current?.open,
        disabled: !trip,
      },
      {
        label: 'Excluir viagem',
        onClick: handleDelete,
        disabled: !trip,
      },
    ];
  }, [tripQuery?.data]);

  const handleNavigateBack = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  React.useEffect(() => {
    return () => {
      resetTripStoreState();
    };
  }, [resetTripStoreState]);

  return (
    <Scaffold>
      <ConfirmationDialog ref={deleteTripDialogRef} />
      <UpdateDeadlineDateDialog
        ref={updateDeadlineDateDialogRef}
        onSubmit={updateDeadlineDateMutation.mutate}
        loading={updateDeadlineDateMutation.isLoading}
      />
      <UpdateTripDriverNameDialog
        ref={updateDriverNameDialogRef}
        onSubmit={updateDriverNameMutation.mutate}
        loading={updateDriverNameMutation.isLoading}
      />
      <UpdateTripVehiclePlateDialog
        ref={updateVehiclePlateDialogRef}
        onSubmit={updateVehiclePlateMutation.mutate}
        loading={updateVehiclePlateMutation.isLoading}
      />
      <PageHeader>
        <Button size={'sm'} variant={'secondary'} onClick={handleNavigateBack}>
          Voltar
        </Button>
      </PageHeader>
      <div className={'flex flex-col gap-6 mt-4'}>
        <div className={'flex flex-col'}>
          <TripMap />
          <TripInfoBar className={'z-10 -mt-3'}>
            <ActionsMenu menuActions={menuActions} />
          </TripInfoBar>
        </div>
        <InvoicesList />
        <div className={'grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6'}>
          <ActivitiesTimeline />
        </div>
      </div>
    </Scaffold>
  );
};
